import { SIGNUP_EVENT } from '@theme/kb-waitlist-form';

const HIDE_DELAY_MS = 1500;

// Both the reveal (the trigger section entering) and the hide (a hide section
// entering) fire this many px AFTER the section's top crosses the viewport's
// bottom edge. Applied as ONE negative bottom rootMargin on the shared observer,
// which shrinks the root's bottom edge for every target it watches — so the bar
// appears, and later disappears, a touch lower into each section instead of on
// its very first pixel.
const EDGE_DELAY_PX = 50;

class KBWaitlistSticky extends HTMLElement {
  #controller = new AbortController();
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #measureRafId = 0;
  #signedUp = false;

  connectedCallback() {
    this.#bindObservers();
    this.#bindHeightPublishing();
    this.#bindZoomGuard();

    document.addEventListener(
      SIGNUP_EVENT,
      (event) => {
        const source = event?.detail?.source;
        if (source === 'sms') return;
        this.#signedUp = true;
        window.setTimeout(() => this.#setVisible(false), HIDE_DELAY_MS);
      },
      { signal: this.#controller.signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    if (this.#measureRafId) cancelAnimationFrame(this.#measureRafId);
  }

  // Publish the bar's REAL height so other sections (the storytelling bottom copy
  // and dots) can sit just above the actual bar, not a guessed value. The bar's
  // height includes env(safe-area-inset-bottom), which changes as the iOS/Chrome
  // browser bar shows and hides, so a fixed number is always wrong in one state
  // or the other. We measure and republish on resize/viewport change instead.
  // This feeds only the storytelling glue (--kb-signup-bar-height), which is below
  // the fold at load, so there is no visible load-time jump; the hero keeps its
  // own fixed clearance token so it never shifts on screen.
  #bindHeightPublishing() {
    this.#publishHeight();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#scheduleHeightPublish());
      this.#resizeObserver.observe(this);
    }

    const republish = () => this.#scheduleHeightPublish();
    window.visualViewport?.addEventListener('resize', republish, { signal: this.#controller.signal });
    window.addEventListener('resize', republish, { signal: this.#controller.signal });
  }

  // iOS Safari zooms the page when an input with font-size < 16px is focused, and
  // it decides from the size AT focus time. The sticky input is intentionally 12px,
  // so on touch we add .is-no-zoom (CSS bumps it to 16px) on touchstart — which
  // fires BEFORE focus — to suppress the zoom, then drop it on blur so the resting
  // size stays 12px. Touch only: desktop never fires touchstart, so it keeps 12px
  // and a mouse focus does not zoom anyway. Listeners are bound to the abort signal
  // so they are cleaned up with the element.
  #bindZoomGuard() {
    const inputs = this.querySelectorAll('.kb-waitlist-form__input');
    inputs.forEach((input) => {
      input.addEventListener('touchstart', () => input.classList.add('is-no-zoom'), {
        passive: true,
        signal: this.#controller.signal,
      });
      input.addEventListener('blur', () => input.classList.remove('is-no-zoom'), {
        signal: this.#controller.signal,
      });
    });
  }

  #scheduleHeightPublish() {
    if (this.#measureRafId) return;
    this.#measureRafId = requestAnimationFrame(() => {
      this.#measureRafId = 0;
      this.#publishHeight();
    });
  }

  #publishHeight() {
    const height = this.offsetHeight;
    if (height > 0) {
      document.documentElement.style.setProperty('--kb-signup-bar-height', `${height}px`);
    }
  }

  #bindObservers() {
    const triggerId = this.dataset.triggerId;
    const hideId = this.dataset.hideId;

    // The bar is gated on a trigger section: it stays hidden until that section
    // ENTERS the viewport, so it never sits over the hero or the
    // animation/storytelling banner (the trigger is the section that follows the
    // animation, e.g. About Karen). It reveals the moment you reach that section
    // — not a screen later — then hides again over any of the hide sections (the
    // gallery / final signup, to avoid a duplicate form) and after signup.
    const triggerEl = triggerId ? document.querySelector(triggerId) : null;
    const hideEls = hideId
      ? hideId
          .split(',')
          .map((sel) => document.querySelector(sel.trim()))
          .filter((el) => el instanceof Element)
      : [];

    // Nothing to gate on: fall back to a permanently visible bar.
    if (!triggerEl && hideEls.length === 0) {
      this.#setVisible(true);
      return;
    }

    this.#hideStates = new WeakMap();
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === triggerEl) {
            // True once the trigger section's top has risen above the (shrunk)
            // root's bottom edge — i.e. it has entered EDGE_DELAY_PX into the
            // viewport. The negative bottom rootMargin below does the delay, so
            // rootBounds.bottom is already viewport height minus that margin. Stays
            // true while you are on or below the section; resets on scroll back up.
            const viewportBottom = entry.rootBounds?.bottom ?? window.innerHeight - EDGE_DELAY_PX;
            this.#triggerPassed = entry.boundingClientRect.top < viewportBottom;
          } else if (this.#hideStates) {
            // isIntersecting is measured against the SAME margin-shrunk root, so a
            // hide section counts as visible only once it is EDGE_DELAY_PX in — the
            // bar therefore hides EDGE_DELAY_PX later too, matching the reveal.
            this.#hideStates.set(entry.target, entry.isIntersecting);
          }
        }
        const anyHideVisible = hideEls.some((el) => this.#hideStates?.get(el) === true);
        // With no trigger configured, treat it as already passed (show by default).
        const passed = triggerEl ? this.#triggerPassed : true;
        this.#setVisible(passed && !anyHideVisible);
      },
      { threshold: [0, 0.1, 0.5, 1], rootMargin: `0px 0px -${EDGE_DELAY_PX}px 0px` }
    );

    if (triggerEl) this.#observer.observe(triggerEl);
    hideEls.forEach((el) => this.#observer?.observe(el));
  }

  #triggerPassed = false;
  /** @type {WeakMap<Element, boolean> | null} */
  #hideStates = null;

  /**
   * @param {boolean} visible
   */
  #setVisible(visible) {
    if (this.#signedUp) visible = false;
    this.dataset.visible = visible ? 'true' : 'false';
    this.inert = !visible;
  }
}

if (!customElements.get('kb-waitlist-sticky')) {
  customElements.define('kb-waitlist-sticky', KBWaitlistSticky);
}

export { KBWaitlistSticky };
